// Disegno di un'area (poligono o rettangolo) con leaflet-draw, comandato dai
// bottoni della barra strumenti invece che dalla toolbar di default.

import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet-draw";

// Messaggi di leaflet-draw in italiano (tooltip che segue il cursore).
const H = L.drawLocal.draw.handlers;
H.polygon.tooltip = { start: "Clicca per iniziare il poligono", cont: "Clicca per continuare", end: "Clicca sul primo punto per chiudere" };
H.polygon.error = "<strong>Errore:</strong> i lati non possono incrociarsi";
H.rectangle.tooltip = { start: "Clicca e trascina per disegnare il rettangolo" };
H.simpleshape.tooltip = { end: "Rilascia il mouse per terminare" };

// Pane proprio: le zone si caricano dopo e altrimenti coprirebbero il disegno.
const DRAW_PANE = "sm-draw";
const DRAW_STYLE = { color: "#84e34c", opacity: 1, weight: 2.5, fillColor: "#84e34c", fillOpacity: 0.15, pane: DRAW_PANE };

export default function useDraw(map, { onCreated } = {}) {
  const groupRef = useRef(null);
  const handlerRef = useRef(null);
  const onCreatedRef = useRef(onCreated);
  onCreatedRef.current = onCreated;

  const [mode, setMode] = useState(null);   // "polygon" | "rectangle" | null
  const [shape, setShape] = useState(null); // Feature GeoJSON dell'ultima area

  useEffect(() => {
    if (!map) return undefined;
    if (!map.getPane(DRAW_PANE)) map.createPane(DRAW_PANE).style.zIndex = 440;
    const group = new L.FeatureGroup().addTo(map);
    groupRef.current = group;

    const created = (e) => {
      group.clearLayers();
      e.layer.setStyle?.(DRAW_STYLE);
      group.addLayer(e.layer);
      const feature = e.layer.toGeoJSON();
      setShape(feature);
      onCreatedRef.current?.(feature);
    };
    const stopped = () => {
      handlerRef.current = null;
      setMode(null);
    };
    map.on(L.Draw.Event.CREATED, created);
    map.on(L.Draw.Event.DRAWSTOP, stopped);
    return () => {
      map.off(L.Draw.Event.CREATED, created);
      map.off(L.Draw.Event.DRAWSTOP, stopped);
      try {
        // la mappa può essere già stata rimossa dal componente che la possiede
        handlerRef.current?.disable();
        map.removeLayer(group);
      } catch { /* niente da pulire */ }
      handlerRef.current = null;
      groupRef.current = null;
    };
  }, [map]);

  const start = useCallback((kind) => {
    if (!map) return;
    handlerRef.current?.disable();
    // showArea: false — in leaflet-draw 1.0.4 il calcolo dell'area nel
    // tooltip va in errore ("type is not defined").
    const opts = { shapeOptions: DRAW_STYLE, showArea: false, metric: true };
    const handler = kind === "rectangle"
      ? new L.Draw.Rectangle(map, opts)
      : new L.Draw.Polygon(map, { ...opts, allowIntersection: false });
    handler.enable();
    handlerRef.current = handler;
    setMode(kind);
  }, [map]);

  const cancel = useCallback(() => {
    handlerRef.current?.disable();
  }, []);

  const clear = useCallback(() => {
    groupRef.current?.clearLayers();
    setShape(null);
  }, []);

  return { mode, shape, start, cancel, clear };
}
